import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { lenders, recommendationMeta } from "@/lib/mock-data";
import { AlertTriangle, Building2, Flag, Plus, Users, MessageSquare, TrendingUp, ShieldAlert, Globe, ScrollText, BellRing } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { isAdmin as fetchIsAdmin } from "@/utils/roles.functions";

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin — LendyTrack" }, { name: "robots", content: "noindex" }] }),
  component: Admin,
});

function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [status, setStatus] = useState<"checking" | "allowed" | "denied">("checking");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate({ to: "/auth" });
      return;
    }
    fetchIsAdmin()
      .then((r) => setStatus(r.isAdmin ? "allowed" : "denied"))
      .catch(() => setStatus("denied"));
  }, [user, authLoading, navigate]);

  if (status !== "allowed") {
    return (
      <AppShell>
        <div className="mx-auto mt-20 max-w-md rounded-2xl border border-border bg-card p-10 text-center shadow-soft">
          <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-danger/10 text-danger">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <h1 className="mt-4 font-display text-xl font-semibold">
            {status === "checking" ? "Verifying access…" : "Admins only"}
          </h1>
          {status === "denied" && (
            <>
              <p className="mt-2 text-sm text-muted-foreground">
                This area is restricted to LendyTrack administrators.
              </p>
              <Link to="/dashboard" className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground">
                Back to dashboard
              </Link>
            </>
          )}
        </div>
      </AppShell>
    );
  }

  const totals = {
    lenders: lenders.length,
    flagged: lenders.filter((l) => l.recommendation === "avoid" || l.recommendation === "high-risk").length,
    complaints: lenders.reduce((a, l) => a + l.complaintBreakdown.reduce((x, y) => x + y.count, 0), 0),
    reviews: lenders.reduce((a, l) => a + l.reviewCount, 0),
  };

  return (
    <AppShell>
      <div className="mb-6 flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Admin</div>
          <h1 className="mt-1 font-display text-3xl font-semibold tracking-tight">Operations dashboard</h1>
        </div>
        <div className="flex gap-2">
          <Link to="/admin-messages" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
            <MessageSquare className="h-4 w-4" /> Inbox
          </Link>
          <Link to="/admin-domain" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
            <Globe className="h-4 w-4" /> Domain
          </Link>
          <Link to="/admin-audit" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
            <ScrollText className="h-4 w-4" /> Audit log
          </Link>
          <Link to="/admin-alert-settings" className="inline-flex items-center gap-2 rounded-md border border-border px-4 py-2 text-sm font-semibold hover:bg-muted">
            <BellRing className="h-4 w-4" /> Alert settings
          </Link>
          <button className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90">
            <Plus className="h-4 w-4" /> Add lender
          </button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        {[
          { icon: Building2, label: "Lenders tracked", v: totals.lenders.toLocaleString() },
          { icon: AlertTriangle, label: "Flagged predatory", v: totals.flagged.toLocaleString(), tone: "text-danger" },
          { icon: MessageSquare, label: "Complaints indexed", v: totals.complaints.toLocaleString() },
          { icon: Users, label: "Reviews analyzed", v: totals.reviews.toLocaleString() },
        ].map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="rounded-xl border border-border bg-card p-5 shadow-soft">
              <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{s.label}</div>
                <Icon className="h-4 w-4 text-muted-foreground" />
              </div>
              <div className={`mt-2 font-display text-3xl font-semibold ${s.tone ?? ""}`}>{s.v}</div>
            </div>
          );
        })}
      </div>

      <div className="mt-8 rounded-2xl border border-border bg-card shadow-soft">
        <div className="flex items-center justify-between border-b border-border p-5">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent">Lender management</div>
            <h2 className="mt-1 font-display text-lg font-semibold">All lenders</h2>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <TrendingUp className="h-3 w-3" /> Updated today
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-left text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="px-5 py-3">Lender</th>
                <th className="px-5 py-3">Category</th>
                <th className="px-5 py-3">Trust</th>
                <th className="px-5 py-3">Predatory</th>
                <th className="px-5 py-3">APR</th>
                <th className="px-5 py-3">Recommendation</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {lenders.map((l) => {
                const meta = recommendationMeta[l.recommendation];
                return (
                  <tr key={l.slug} className="border-t border-border hover:bg-muted/30">
                    <td className="px-5 py-3 font-semibold">{l.name}</td>
                    <td className="px-5 py-3 text-muted-foreground">{l.category}</td>
                    <td className="px-5 py-3 tabular-nums">{l.scores.trust}</td>
                    <td className="px-5 py-3 tabular-nums">{l.scores.predatory}</td>
                    <td className="px-5 py-3 tabular-nums">{l.aprRange[0]}–{l.aprRange[1]}%</td>
                    <td className="px-5 py-3">
                      <span className={`rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-widest ${meta.bg} ${meta.tone}`}>
                        {meta.label}
                      </span>
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button className="rounded-md p-1.5 text-muted-foreground hover:bg-danger/10 hover:text-danger" aria-label="Flag">
                        <Flag className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-6 grid gap-4 md:grid-cols-2">
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <h3 className="font-display text-base font-semibold">Top complaint themes</h3>
          <div className="mt-3 space-y-2">
            {Object.entries(
              lenders.flatMap((l) => l.complaintBreakdown).reduce<Record<string, number>>((acc, c) => {
                acc[c.category] = (acc[c.category] ?? 0) + c.count;
                return acc;
              }, {}),
            )
              .sort((a, b) => b[1] - a[1])
              .slice(0, 6)
              .map(([cat, count]) => (
                <div key={cat} className="flex items-center justify-between rounded-md bg-muted px-3 py-2 text-sm">
                  <span>{cat}</span>
                  <span className="font-semibold tabular-nums">{count.toLocaleString()}</span>
                </div>
              ))}
          </div>
        </div>
        <div className="rounded-xl border border-border bg-card p-5 shadow-soft">
          <h3 className="font-display text-base font-semibold">AI scoring weights</h3>
          <div className="mt-3 space-y-3 text-sm">
            {[
              { label: "Complaint volume", w: 30 },
              { label: "Sentiment", w: 25 },
              { label: "APR & fees", w: 25 },
              { label: "Regulatory history", w: 12 },
              { label: "Transparency", w: 8 },
            ].map((w) => (
              <div key={w.label}>
                <div className="flex justify-between text-xs">
                  <span>{w.label}</span>
                  <span className="font-semibold">{w.w}%</span>
                </div>
                <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-muted">
                  <div className="h-full rounded-full bg-accent" style={{ width: `${w.w}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </AppShell>
  );
}
